import React, { useEffect, useRef } from 'react';
import { Search, PenTool, FileText, HardHat, CheckCircle, Award, Clock, DollarSign, ArrowRight } from 'lucide-react';

const useScrollAnimation = (threshold = 0.06) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// ── Process steps ───────────────────────────────────────────────────────────
const steps = [
  {
    number: '01',
    icon: Search,
    title: 'Análisis del terreno y necesidades',
    description:
      'Realizamos una visita técnica para evaluar las condiciones del terreno, factibilidad de construcción, normativa local y requerimientos específicos del cliente.',
    details: ['Visita técnica al terreno', 'Evaluación de factibilidad', 'Análisis de normativa local'],
  },
  {
    number: '02',
    icon: PenTool,
    title: 'Diseño arquitectónico y técnico',
    description:
      'Nuestro equipo de arquitectos e ingenieros desarrolla los planos arquitectónicos, estructurales y de instalaciones, adaptados a tus necesidades y estilo.',
    details: ['Planos arquitectónicos', 'Diseño estructural', 'Especificaciones técnicas'],
  },
  {
    number: '03',
    icon: FileText,
    title: 'Presupuesto detallado y transparente',
    description:
      'Presentamos un presupuesto desglosado por conceptos, sin costos ocultos ni letra chica: materiales, mano de obra, tiempos de entrega y condiciones de pago.',
    details: ['Desglose por conceptos', 'Sin costos ocultos', 'Calendario de pagos'],
  },
  {
    number: '04',
    icon: HardHat,
    title: 'Ejecución con supervisión y control',
    description:
      'Iniciamos la obra con personal calificado y residentes de obra permanentes, con control estricto de calidad, avance y presupuesto en cada etapa.',
    details: ['Residentes de obra permanentes', 'Reportes de avance periódicos', 'Control de calidad continuo'],
  },
  {
    number: '05',
    icon: CheckCircle,
    title: 'Entrega con seguimiento',
    description:
      'Entregamos el proyecto terminado con recorrido formal de recepción, documentación de garantías y soporte post-entrega para cualquier ajuste necesario.',
    details: ['Recorrido y recepción formal', 'Documentación de garantías', 'Soporte post-entrega'],
  },
];

// ── Commitment pillars ──────────────────────────────────────────────────────
const pillars = [
  { icon: Award,       label: 'Calidad',  detail: 'Materiales y mano de obra certificados' },
  { icon: Clock,       label: 'Tiempo',   detail: 'Cumplimiento del calendario pactado' },
  { icon: DollarSign,  label: 'Costo',    detail: 'Presupuesto sin sorpresas ni extras' },
];

// ── Component ────────────────────────────────────────────────────────────────
const HowWeWork = () => {
  const ref = useScrollAnimation();

  return (
    <section
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--bg-primary)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background grid decoration */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(var(--border-color) 1px, transparent 1px), linear-gradient(90deg, var(--border-color) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        opacity: 0.35,
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto' }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <div className="section-label">Nuestro proceso</div>
          <h2 style={{
            fontFamily: 'var(--font-heading)', fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', marginTop: '0.5rem',
          }}>
            Cómo <span style={{ color: 'var(--red)' }}>trabajamos</span>
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '1rem', color: 'var(--gray)',
            maxWidth: 520, margin: '1rem auto 0', lineHeight: 1.75,
          }}>
            Un proceso profesional, estructurado y transparente de principio a fin,
            para que tu proyecto avance sin sorpresas.
          </p>
        </div>

        {/* ── VERTICAL TIMELINE ──────────────────────────────────────────── */}
        <div style={{ position: 'relative' }}>

          {/* Vertical line (desktop) */}
          <div style={{
            position: 'absolute',
            left: 39,
            top: 20,
            bottom: 20,
            width: 2,
            background: 'linear-gradient(to bottom, var(--red) 0%, rgba(196,30,58,0.1) 100%)',
            zIndex: 0,
          }} className="hww-line" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === steps.length - 1;
              return (
                <div
                  key={i}
                  className={`fade-up delay-${i + 1}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr',
                    gap: '2rem',
                    paddingBottom: isLast ? 0 : '2.5rem',
                    position: 'relative',
                  }}
                >
                  {/* ── Left: number + icon circle ── */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: i === 0 ? 'var(--red)' : 'var(--bg-secondary)',
                      border: `2px solid ${i === 0 ? 'var(--red)' : 'var(--border-color)'}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: i === 0 ? '0 8px 32px rgba(196,30,58,0.3)' : '0 4px 16px rgba(0,0,0,0.07)',
                      flexShrink: 0,
                      transition: 'all 0.3s ease',
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-heading)', fontWeight: 900,
                        fontSize: '0.58rem', letterSpacing: '0.12em',
                        color: i === 0 ? 'rgba(255,255,255,0.75)' : 'var(--gray)',
                        marginBottom: 2,
                      }}>
                        {step.number}
                      </span>
                      <Icon
                        size={24}
                        color={i === 0 ? 'white' : 'var(--red)'}
                        strokeWidth={1.75}
                      />
                    </div>
                  </div>

                  {/* ── Right: content card ── */}
                  <div style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderLeft: i === 0 ? '4px solid var(--red)' : '1px solid var(--border-color)',
                    borderRadius: 16,
                    padding: '1.75rem 2rem',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)';
                    if (i !== 0) e.currentTarget.style.borderLeftColor = 'rgba(196,30,58,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)';
                    if (i !== 0) e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                  }}
                  >
                    <h3 style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 800,
                      fontSize: '1.05rem', color: 'var(--text-primary)',
                      lineHeight: 1.3, marginBottom: '0.6rem',
                    }}>
                      {step.title}
                    </h3>
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.875rem',
                      color: 'var(--gray-dark)', lineHeight: 1.8,
                      marginBottom: '1rem',
                    }}>
                      {step.description}
                    </p>

                    {/* Detail tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {step.details.map((d, j) => (
                        <span key={j} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                          background: i === 0 ? 'rgba(196,30,58,0.08)' : 'var(--bg-secondary)',
                          border: `1px solid ${i === 0 ? 'rgba(196,30,58,0.18)' : 'var(--border-color)'}`,
                          color: i === 0 ? 'var(--red)' : 'var(--gray-dark)',
                          fontFamily: 'var(--font-heading)', fontWeight: 600,
                          fontSize: '0.68rem', letterSpacing: '0.06em',
                          padding: '0.3rem 0.7rem', borderRadius: 20,
                        }}>
                          <div style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: i === 0 ? 'var(--red)' : 'var(--gray)',
                          }} />
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── COMMITMENT BAR ─────────────────────────────────────────────── */}
        <div className="fade-up" style={{ marginTop: '4.5rem' }}>
          <div style={{
            background: 'var(--band-bg)',
            boxShadow: 'inset 0 0 0 1px var(--band-ring)',
            borderRadius: 20,
            padding: '2.5rem 3rem',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* glow */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'radial-gradient(ellipse at 30% 50%, rgba(196,30,58,0.18) 0%, transparent 65%)',
              pointerEvents: 'none',
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Statement */}
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <p style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 900,
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', color: 'var(--band-text)',
                  lineHeight: 1.3,
                }}>
                  Cumplimos en{' '}
                  <span style={{ color: 'var(--red)' }}>calidad</span>,{' '}
                  <span style={{ color: 'var(--red)' }}>tiempo</span>{' '}
                  y{' '}
                  <span style={{ color: 'var(--red)' }}>costo</span>
                </p>
                <p style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.875rem',
                  color: 'var(--band-text-50)', marginTop: '0.5rem',
                }}>
                  Nuestro compromiso es entregar exactamente lo que prometemos, en el plazo acordado y sin sobrecostos.
                </p>
              </div>

              {/* 3 pillars */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1px',
                background: 'var(--band-gap)',
                borderRadius: 12,
                overflow: 'hidden',
              }} className="hww-pillars">
                {pillars.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <div key={i} style={{
                      background: 'var(--band-surface)',
                      padding: '1.5rem',
                      textAlign: 'center',
                    }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        background: 'var(--red)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 0.875rem',
                        boxShadow: '0 6px 20px rgba(196,30,58,0.35)',
                      }}>
                        <Icon size={22} color="white" strokeWidth={1.75} />
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-heading)', fontWeight: 900,
                        fontSize: '1.1rem', color: 'var(--band-text)', marginBottom: '0.3rem',
                      }}>
                        {p.label}
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                        color: 'var(--band-text-45)', lineHeight: 1.5,
                      }}>
                        {p.detail}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ textAlign: 'center', marginTop: '3rem' }}>
          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-red"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Recibe tu propuesta personalizada <ArrowRight size={16} />
          </button>
        </div>

      </div>

      <style>{`
        @media (max-width: 700px) {
          .hww-line { display: none !important; }
          .hww-pillars { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 540px) {
          /* Stack number + card vertically on small screens */
          .hww-step { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default HowWeWork;
